import type { FC, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import IcBaselineAccountCircle from '~icons/ic/baseline-account-circle'
import type { User } from 'src/services/apollo/generated/graphql'
import { track } from 'src/utils/tracker'
import { getUserDisplayName } from 'src/utils/user'
import { cx } from '../../utils/cx'
import UserAvatar from '../UserAvatar'

const UserLink: FC<{
  user?: User
  name?: string
  size?: number
  showName?: boolean
  showUserName?: boolean
  showFollow?: boolean
  leftAnchor?: ReactNode
  loading?: boolean
  enableLink?: boolean
  target?: '_blank'
  className?: string
  linkClassName?: string
  scene?: string
}> = ({
  user,
  name,
  size = 28,
  showName = true,
  showUserName = false,
  leftAnchor,
  loading,
  enableLink = true,
  showFollow = false,
  target,
  className,
  linkClassName,
  scene,
}) => {
  const authorName = name || getUserDisplayName(user)
  const { t } = useTranslation(['common'])

  const content = (
    <div className="flex gap-1 items-center">
      {leftAnchor}
      <div className="no-underline shrink-0">
        {loading ? (
          <div
            className="rounded-full bg-active animate-pulse"
            style={{
              width: size,
              height: size,
            }}
          ></div>
        ) : user ? (
          <UserAvatar
            user={user}
            style={{
              width: size,
              height: size,
            }}
          />
        ) : (
          <IcBaselineAccountCircle className="size-6" />
        )}
      </div>
      {showName ? (
        loading ? (
          <div className="min-w-[100px] h-4 bg-active animate-pulse"></div>
        ) : (
          <div
            className={cx(
              'flex flex-col min-w-0',
              showFollow ? 'ml-3 gap-2' : 'ml-1',
            )}
          >
            <div className="flex flex-col gap-0">
              <p
                className={cx(
                  'truncate text-base font-semibold group-hover:underline decoration-current',
                  className,
                )}
              >
                {authorName}
              </p>
              {user?.username && showUserName && (
                <div
                  className={cx(
                    'truncate text-sm group-hover:underline decoration-current text-gray-500',
                  )}
                >
                  @{user?.username}
                </div>
              )}
            </div>

            {showFollow && (
              <div className="">
                <div className="flex gap-2 items-center">
                  <div className="flex gap-1 items-center">
                    <span className="font-bold">
                      {(user?.followingCount || 0).toLocaleString()}
                    </span>
                    <span className="text-sm text-primary-light">
                      {t('user:following-count-label')}
                    </span>
                  </div>

                  <div className="flex gap-1 items-center">
                    <span className="font-bold">
                      {(user?.followerCount || 0).toLocaleString()}
                    </span>
                    <span className="text-sm text-primary-light">
                      {t('user:follower-count-label')}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )
      ) : null}
    </div>
  )

  if (user && user.username && enableLink) {
    return (
      <Link
        to={`/@${user.username}`}
        target={target}
        className={cx('group no-underline', linkClassName)}
        onClick={() => {
          track('user_avatar_click', { scene })
        }}
      >
        {content}
      </Link>
    )
  }

  return content
}

export default UserLink
